import { listMods, voteMod, type ModListItem } from '../api/mods';
import { setModAuthors } from '../ui/mod-authors';
import { state } from '../state';
import { t, onLocaleChange } from '../i18n';
import { navigateTo } from '../router';
import { ensureRolesLoaded } from '../app';
import { initModModal, openAddModModal, openEditModModal, type ModModalResult } from '../ui/mod-modal';
import { openModExclusionsModal } from '../ui/mod-exclusions';
import { attachWhoTooltip, whoNames } from '../ui/admin-who';

// ============================================================
// Публичный раздел «Моды»: список модов с лайками/дизлайками.
// Настройки мода показываются свёрнутым списком. Голосовать можно только
// авторизованным (иначе редирект на вход).
// ============================================================

let abort: AbortController | null = null;
let items: ModListItem[] = [];
let query = '';
let activeAuthor: string | null = null;

let searchInput: HTMLInputElement | null = null;
let filterBar: HTMLElement | null = null;
let listEl: HTMLElement | null = null;

// --- Права на добавление/правку модов (модератор / ghost / админ, как на сервере) ---
let canEdit = false;

// Живой пересчёт текста при смене языка
onLocaleChange(() => {
    if (!document.getElementById('mods-container')) return;
    if (searchInput) searchInput.placeholder = t('mods.search_placeholder');
    renderFilters();
    renderList();
});

export function mountMods(): void {
    const screen = document.getElementById('screen-mods');
    if (screen) screen.classList.remove('hidden');

    const container = document.getElementById('mods-container');
    if (!container) return;

    abort = new AbortController();
    const { signal } = abort;

    initModModal();
    bindAddButton(signal);
    buildLayout(container);

    // Роли подгружаются асинхронно — обновляем права после их получения.
    void ensureRolesLoaded().then(updateEditPermission);
    updateEditPermission();

    void load();
}

export function unmountMods(): void {
    abort?.abort();
    abort = null;
    searchInput = null;
    filterBar = null;
    listEl = null;
}

/** Кнопка «Добавить мод» открывает общую модалку мода. */
function bindAddButton(signal: AbortSignal): void {
    document.getElementById('btn-mods-add')?.addEventListener('click', () => {
        void openAddModModal().then((saved) => {
            if (saved) upsertModInList(saved);
        });
    }, { signal });
}

/** Показывает кнопку добавления и делает карточки редактируемыми для модераторов. */
function updateEditPermission(): void {
    canEdit = Boolean(state.user?.is_moderator || state.user?.is_admin || state.user?.is_ghost);
    const addBtn = document.getElementById('btn-mods-add');
    if (addBtn) {
        if (canEdit) addBtn.removeAttribute('hidden');
        else addBtn.setAttribute('hidden', '');
    }
    if (listEl && items.length > 0) renderList();
}

async function load(): Promise<void> {
    if (!listEl) return;
    listEl.innerHTML = `<p class="hint">${t('common.loading')}</p>`;
    try {
        items = await listMods(state.token);
    } catch (err) {
        console.error('[mods] load error:', err);
        if (listEl) listEl.innerHTML = `<p class="error">${t('mods.error')}</p>`;
        return;
    }
    if (abort?.signal.aborted) return;
    // Подсказки авторов для модалки — из уже загруженного списка.
    setModAuthors(items.map((m) => m.author));
    renderFilters();
    renderList();
}

function buildLayout(container: HTMLElement): void {
    container.innerHTML = '';

    const controls = document.createElement('div');
    controls.className = 'maps-controls';

    searchInput = document.createElement('input');
    searchInput.type = 'search';
    searchInput.className = 'maps-search';
    searchInput.placeholder = t('mods.search_placeholder');
    searchInput.value = query;
    searchInput.addEventListener('input', () => {
        query = searchInput!.value.trim().toLowerCase();
        renderList();
    });
    controls.appendChild(searchInput);

    filterBar = document.createElement('div');
    filterBar.className = 'maps-filters';
    controls.appendChild(filterBar);

    container.appendChild(controls);

    listEl = document.createElement('div');
    listEl.className = 'mods-list';
    container.appendChild(listEl);

    renderFilters();
}

function renderFilters(): void {
    if (!filterBar) return;
    filterBar.innerHTML = '';

    // Группа по автору — выпадающий список, только если авторы указаны.
    const authors = [...new Set(items.map((m) => m.author).filter((a): a is string => !!a))]
        .sort((a, b) => a.localeCompare(b));
    if (activeAuthor != null && !authors.includes(activeAuthor)) activeAuthor = null;
    if (authors.length === 0) return;

    const authorGroup = document.createElement('div');
    authorGroup.className = 'maps-filter-group';

    const label = document.createElement('span');
    label.className = 'maps-filter-label';
    label.textContent = t('mods.filter_author');
    authorGroup.appendChild(label);

    const select = document.createElement('select');
    select.className = 'maps-filter-select';
    select.title = t('mods.filter_author');

    const allOption = document.createElement('option');
    allOption.value = '';
    allOption.textContent = t('mods.filter_all_authors');
    select.appendChild(allOption);

    for (const a of authors) {
        const opt = document.createElement('option');
        opt.value = a;
        opt.textContent = a;
        if (a === activeAuthor) opt.selected = true;
        select.appendChild(opt);
    }

    select.addEventListener('change', () => {
        activeAuthor = select.value || null;
        renderList();
    });

    authorGroup.appendChild(select);
    filterBar.appendChild(authorGroup);
}

function matchesQuery(m: ModListItem): boolean {
    if (!query) return true;
    return (
        m.name.toLowerCase().includes(query) ||
        (m.author ?? '').toLowerCase().includes(query) ||
        (m.description ?? '').toLowerCase().includes(query)
    );
}

function visibleMods(): ModListItem[] {
    return items.filter((m) => {
        if (activeAuthor != null && m.author !== activeAuthor) return false;
        return matchesQuery(m);
    });
}

function renderList(): void {
    if (!listEl) return;
    listEl.innerHTML = '';

    const list = visibleMods();
    if (list.length === 0) {
        const p = document.createElement('p');
        p.className = 'hint';
        p.textContent = t('mods.empty');
        listEl.appendChild(p);
        return;
    }

    for (const m of list) listEl.appendChild(buildCard(m));
}

function buildCard(m: ModListItem): HTMLElement {
    const card = document.createElement('div');
    card.className = 'mod-card';
    card.dataset.modId = String(m.id);

    // --- Текст ---
    const body = document.createElement('div');
    body.className = 'mod-card-body';

    const head = document.createElement('div');
    head.className = 'mod-card-head';

    const title = document.createElement('div');
    title.className = 'mod-card-title';

    const name = document.createElement('div');
    name.className = 'mod-card-name';
    name.textContent = m.name;
    title.appendChild(name);

    if (m.author) {
        const author = document.createElement('div');
        author.className = 'map-card-author';
        author.textContent = t('mods.author', { name: m.author });
        title.appendChild(author);
    }
    head.appendChild(title);

    // Бейджи: популярность (число игр) и число наборов настроек.
    const stats = document.createElement('div');
    stats.className = 'mod-card-stats';

    const gamesBadge = document.createElement('span');
    gamesBadge.className = 'mod-badge mod-badge--games';
    gamesBadge.title = t('mods.games_count_hint');
    gamesBadge.textContent = t('mods.games_count', { n: m.games_count });
    stats.appendChild(gamesBadge);

    const paramCount = m.settings?.params.length ?? 0;
    if (paramCount > 0) {
        const settingsBadge = document.createElement('span');
        settingsBadge.className = 'mod-badge';
        settingsBadge.textContent = t('mods.settings_count', { n: paramCount });
        stats.appendChild(settingsBadge);
    }
    head.appendChild(stats);
    body.appendChild(head);

    if (m.description) {
        const desc = document.createElement('p');
        desc.className = 'mod-card-desc';
        desc.textContent = m.description;
        desc.title = m.description;
        body.appendChild(desc);
    }

    if (m.settings && paramCount > 0) body.appendChild(buildSettings(m));

    card.appendChild(body);

    // --- Голоса ---
    const votes = document.createElement('div');
    votes.className = 'map-card-votes';

    const likeBtn = document.createElement('button');
    likeBtn.type = 'button';
    likeBtn.className = 'map-vote map-vote--like';
    if (m.my_vote === 1) likeBtn.classList.add('is-active');
    likeBtn.title = t('mods.like');
    likeBtn.innerHTML = `<span class="map-vote-icon">👍</span><span class="map-vote-count">${m.likes}</span>`;
    likeBtn.addEventListener('click', () => void onVote(m, 1));
    attachWhoTooltip(likeBtn, 'mod', m.id, (who) =>
      who.votes.up.length > 0 ? `${t('who.likes')}: ${whoNames(who.votes.up)}` : null,
    );

    const dislikeBtn = document.createElement('button');
    dislikeBtn.type = 'button';
    dislikeBtn.className = 'map-vote map-vote--dislike';
    if (m.my_vote === -1) dislikeBtn.classList.add('is-active');
    dislikeBtn.title = t('mods.dislike');
    dislikeBtn.innerHTML = `<span class="map-vote-icon">👎</span><span class="map-vote-count">${m.dislikes}</span>`;
    dislikeBtn.addEventListener('click', () => void onVote(m, -1));
    attachWhoTooltip(dislikeBtn, 'mod', m.id, (who) =>
      who.votes.down.length > 0 ? `${t('who.dislikes')}: ${whoNames(who.votes.down)}` : null,
    );

    votes.append(likeBtn, dislikeBtn);
    card.appendChild(votes);

    // Клик по карточке открывает правку мода (для модератора/ghost/админа).
    if (canEdit) {
        card.classList.add('is-editable');
        card.title = t('mods.edit_hint');
        card.addEventListener('click', (e) => {
            // Кнопки голосования и раскрытие настроек не должны открывать редактор.
            if ((e.target as HTMLElement).closest('.map-card-votes')) return;
            if ((e.target as HTMLElement).closest('.mod-card-settings')) return;
            void openEditModModal(m).then((saved) => {
                if (saved) upsertModInList(saved);
            });
        });
    }

    return card;
}

/**
 * Свёрнутый набор настроек мода.
 * Раскрывается плавно (grid 0fr → 1fr), раскрытый блок ограничен по высоте
 * и прокручивается внутри — большой набор не растягивает страницу и не
 * увеличивает ширину плашки.
 * Модераторам доступна кнопка «Исключения» — какие параметры/значения
 * не должны выпадать при рандомизации.
 */
function buildSettings(m: ModListItem): HTMLElement {
    const settings = m.settings!;
    const presets = m.presets;
    const box = document.createElement('div');
    box.className = 'mod-card-settings';

    const head = document.createElement('div');
    head.className = 'mod-card-settings-head';

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'mod-card-settings-toggle';
    toggle.textContent = t('mods.settings_count', { n: settings.params.length });
    toggle.setAttribute('aria-expanded', 'false');
    head.appendChild(toggle);

    if (canEdit) {
        // Сколько всего исключено: параметры целиком + отдельные значения
        const excludedTotal = settings.params.reduce(
            (sum, p) => sum + (p.disabled ? 1 : 0) + (p.disabled ? 0 : (p.excluded_values ?? []).length),
            0
        );
        const exclBtn = document.createElement('button');
        exclBtn.type = 'button';
        exclBtn.className = 'mod-card-settings-exclusions';
        exclBtn.textContent =
            t('mods.exclusions_button') + (excludedTotal > 0 ? ` (${excludedTotal})` : '');
        exclBtn.title = t('mods.exclusions_button_hint');
        exclBtn.addEventListener('click', () => void editExclusions(m, box));
        head.appendChild(exclBtn);
    }
    box.appendChild(head);

    // Две обёртки: collapse анимирует высоту, inner обрезает содержимое
    // (padding/фон живут внутри, поэтому в свёрнутом виде ничего не видно).
    const collapse = document.createElement('div');
    collapse.className = 'mod-card-settings-collapse';
    const inner = document.createElement('div');
    inner.className = 'mod-card-settings-inner';

    const scroll = document.createElement('div');
    scroll.className = 'mod-card-settings-body';

    // Пресеты — сверху, чтобы были видны без прокрутки списка параметров.
    if (presets.length > 0) {
        const presetsBox = document.createElement('div');
        presetsBox.className = 'mod-presets-list';

        const label = document.createElement('span');
        label.className = 'mod-presets-label';
        label.textContent = t('mods.presets_label');
        presetsBox.appendChild(label);

        for (const preset of presets) {
            const chip = document.createElement('span');
            chip.className = 'mod-preset-chip';
            chip.textContent = preset.name;
            const values = Object.entries(preset.values).map(([k, v]) => `${k}: ${v}`);
            chip.title = values.length > 0 ? values.join(', ') : t('mods.preset_no_values');
            presetsBox.appendChild(chip);
        }

        scroll.appendChild(presetsBox);
    }

    const group = document.createElement('section');
    group.className = 'mod-setting';

    // Параметры в колонки — раскрытие не вытягивает плашку в длину.
    const grid = document.createElement('div');
    grid.className = 'mod-params-grid';

    for (const param of settings.params) {
        const row = document.createElement('div');
        row.className = 'mod-param' + (param.depends_on ? ' mod-param--child' : '');

        const paramName = document.createElement('span');
        paramName.className = 'mod-param-name';
        // Вложенные параметры помечаем стрелкой — видно, что они по ветвлению
        paramName.textContent = (param.depends_on ? '↳ ' : '') + `${param.name}:`;

        const values = document.createElement('span');
        values.className = 'mod-param-values';
        values.textContent = param.values.length > 0 ? param.values.join(' / ') : '—';

        row.append(paramName, values);

        // Метки: значение по умолчанию и «не рандомизируется».
        const tags = document.createElement('span');
        tags.className = 'mod-param-tags';

        if (param.default_value) {
            const def = document.createElement('span');
            def.className = 'mod-param-tag mod-param-tag--default';
            def.textContent = t('mods.param_default_short', { value: param.default_value });
            def.title = t('mods.param_default_hint');
            tags.appendChild(def);
        }
        if (param.randomize === false) {
            const fixed = document.createElement('span');
            fixed.className = 'mod-param-tag mod-param-tag--fixed';
            fixed.textContent = t('mods.param_fixed_short');
            fixed.title = t('mods.param_fixed_hint');
            tags.appendChild(fixed);
        }
        // Исключения из рандомизации: параметр целиком или отдельные значения
        if (param.disabled) {
            const excluded = document.createElement('span');
            excluded.className = 'mod-param-tag mod-param-tag--excluded';
            excluded.textContent = t('mods.param_disabled_short');
            excluded.title = t('mods.exclusions_button_hint');
            tags.appendChild(excluded);
        } else if ((param.excluded_values ?? []).length > 0) {
            const excluded = document.createElement('span');
            excluded.className = 'mod-param-tag mod-param-tag--excluded';
            excluded.textContent = t('mods.param_excluded_short', { n: param.excluded_values.length });
            excluded.title = param.excluded_values.join(', ');
            tags.appendChild(excluded);
        }
        // Ветвление: параметр появляется только при определённом значении родителя
        if (param.depends_on) {
            const dep = document.createElement('span');
            dep.className = 'mod-param-tag mod-param-tag--dep';
            dep.textContent = t('mods.param_depends_short', {
                param: param.depends_on,
                values: (param.depends_values ?? []).join(' / '),
            });
            dep.title = t('mods.param_depends_hint');
            tags.appendChild(dep);
        }
        if (tags.childElementCount > 0) row.appendChild(tags);

        grid.appendChild(row);
    }

    group.appendChild(grid);
    scroll.appendChild(group);
    inner.appendChild(scroll);
    collapse.appendChild(inner);
    box.appendChild(collapse);

    toggle.addEventListener('click', () => {
        const open = box.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', String(open));
    });

    return box;
}

/**
 * Окно «Исключения из рандомизации» (модератор/ghost/админ).
 * После сохранения обновляем только блок настроек карточки — раскрытое
 * состояние и позиция в списке сохраняются.
 */
async function editExclusions(m: ModListItem, box: HTMLElement): Promise<void> {
    const result = await openModExclusionsModal(
        { id: m.id, name: m.name, settings: m.settings, presets: m.presets },
        { canEditShared: true, token: state.token }
    );
    if (!result) return;
    m.settings = result.settings;

    const wasOpen = box.classList.contains('is-open');
    const next = buildSettings(m);
    if (wasOpen) {
        next.classList.add('is-open');
        next.querySelector('.mod-card-settings-toggle')?.setAttribute('aria-expanded', 'true');
    }
    box.replaceWith(next);
}

async function onVote(m: ModListItem, value: 1 | -1): Promise<void> {
    if (!state.user) {
        // Голосовать могут только авторизованные
        navigateTo('/auth');
        return;
    }
    const next = (m.my_vote === value ? 0 : value) as 1 | -1 | 0;
    try {
        const r = await voteMod(m.id, next, state.token);
        m.likes = r.likes;
        m.dislikes = r.dislikes;
        m.my_vote = r.my_vote;
        renderList();
    } catch (err) {
        console.error('[mods] vote error:', err);
    }
}

/** Вставляет карточку в правильную позицию видимого списка (без перерисовки). */
function insertVisibleCard(item: ModListItem): void {
    if (!listEl) return;
    const orderIds = visibleMods().map((m) => m.id);
    const pos = orderIds.indexOf(item.id);
    const nodes = [...listEl.querySelectorAll<HTMLElement>('.mod-card')];
    let before: Node | null = null;
    for (const n of nodes) {
        const np = orderIds.indexOf(Number(n.dataset.modId));
        if (np > pos) { before = n; break; }
    }
    listEl.insertBefore(buildCard(item), before);
    listEl.querySelector('.hint')?.remove();
}

/**
 * Добавляет/обновляет мод в списке без перезагрузки страницы: меняем только
 * его карточку (или вставляем/удаляем), поиск и фильтры не сбрасываются.
 * Голоса сохраняем из прежней записи — модалка их не присылает.
 */
function upsertModInList(result: ModModalResult): void {
    if (!listEl) return;
    const prev = items.find((m) => m.id === result.id);
    const item: ModListItem = {
        id: result.id,
        name: result.name,
        author: result.author,
        description: result.description,
        settings: result.settings,
        presets: result.presets,
        likes: prev?.likes ?? 0,
        dislikes: prev?.dislikes ?? 0,
        games_count: prev?.games_count ?? 0,
        my_vote: prev?.my_vote ?? 0,
    };

    const idx = items.findIndex((m) => m.id === item.id);
    if (idx === -1) items.push(item);
    else items[idx] = item;
    // Сортировка как на сервере — по популярности (числу игр).
    items.sort((a, b) => b.games_count - a.games_count || a.name.localeCompare(b.name));

    // Набор авторов мог измениться — обновляем фильтр.
    renderFilters();

    const isVisible = visibleMods().some((m) => m.id === item.id);
    const existing = listEl.querySelector<HTMLElement>(`.mod-card[data-mod-id="${item.id}"]`);

    if (!isVisible) {
        existing?.remove();
        if (!listEl.querySelector('.mod-card')) {
            listEl.innerHTML = `<p class="hint">${t('mods.empty')}</p>`;
        }
        return;
    }

    existing?.remove();
    insertVisibleCard(item);
}
